import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { EmbedToken, EmbedTokenPayload, EmbedTheme, EmbedWidgetType, embedTokenApi, StorefrontAdminProduct } from '../../../services/storefrontService';

const TYPES: EmbedWidgetType[] = ['catalog', 'course_card', 'landing', 'buy_button'];
const THEMES: EmbedTheme[] = ['auto', 'light', 'dark'];
const NEEDS_PRODUCT: EmbedWidgetType[] = ['course_card', 'buy_button'];

export interface EmbedTokenFormDialogProps {
    open: boolean;
    /** `null` = neues Widget */
    embed: EmbedToken | null;
    products: StorefrontAdminProduct[];
    onClose: () => void;
    onSaved: (embed: EmbedToken, created: boolean) => void;
}

const EmbedTokenFormDialog: React.FC<EmbedTokenFormDialogProps> = ({ open, embed, products, onClose, onSaved }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const tr = (key: string) => translate(`embed.form.${key}`);
    const published = products.filter((p) => p.status === 'published');
    const [label, setLabel] = useState('');
    const [type, setType] = useState<EmbedWidgetType>('catalog');
    const [theme, setTheme] = useState<EmbedTheme>('auto');
    const [productId, setProductId] = useState<number | ''>('');
    const [origins, setOrigins] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLabel(embed?.label ?? '');
        setType(embed?.widgetType ?? 'catalog');
        setTheme(embed?.config.theme ?? 'auto');
        setProductId(embed?.config.productId ?? '');
        setOrigins((embed?.allowedOrigins ?? []).join('\n'));
        setError(null);
    }, [open, embed]);

    const needsProduct = NEEDS_PRODUCT.includes(type);

    const save = async () => {
        if (needsProduct && productId === '') {
            setError(tr('productRequired'));
            return;
        }
        const payload: EmbedTokenPayload = {
            label: label.trim(),
            widgetType: type,
            allowedOrigins: origins.split('\n').map((o) => o.trim()).filter(Boolean),
            config: { theme, ...(needsProduct && productId !== '' ? { productId } : {}) },
        };
        setSaving(true);
        setError(null);
        try {
            const saved = embed
                ? await embedTokenApi.update(embed.id, { label: payload.label, allowedOrigins: payload.allowedOrigins, config: payload.config })
                : await embedTokenApi.create(payload);
            notify(translate('embed.saved'), { type: 'success' });
            onSaved(saved, embed === null);
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.response?.data?.error ?? e?.response?.data?.detail ?? translate('embed.error'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="embed-form-title">
            <DialogTitle id="embed-form-title">{tr(embed ? 'editTitle' : 'createTitle')}</DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
                {error ? <Alert severity="error">{error}</Alert> : null}
                <TextField label={tr('label')} value={label} onChange={(e) => setLabel(e.target.value)} inputProps={{ maxLength: 120 }} required fullWidth />
                <TextField select label={tr('type')} value={type} onChange={(e) => setType(e.target.value as EmbedWidgetType)} disabled={embed !== null} helperText={embed ? tr('typeFixed') : translate(`embed.types.${type}.description`)} fullWidth>
                    {TYPES.map((t) => <MenuItem key={t} value={t}>{translate(`embed.types.${t}.label`)}</MenuItem>)}
                </TextField>
                {needsProduct ? (
                    published.length === 0 ? (
                        <Alert severity="info">{tr('noProducts')}</Alert>
                    ) : (
                        <TextField select label={tr('product')} value={productId} onChange={(e) => setProductId(Number(e.target.value))} fullWidth>
                            {published.map((p) => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
                        </TextField>
                    )
                ) : null}
                <TextField select label={tr('theme')} value={theme} onChange={(e) => setTheme(e.target.value as EmbedTheme)} fullWidth>
                    {THEMES.map((t) => <MenuItem key={t} value={t}>{translate(`embed.themes.${t}`)}</MenuItem>)}
                </TextField>
                <TextField label={tr('origins')} value={origins} onChange={(e) => setOrigins(e.target.value)} multiline minRows={3} placeholder="https://www.beispiel.de" fullWidth />
                <Typography variant="caption" color="text.secondary">{tr('originsHelp')}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{tr('cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving || label.trim() === ''}>{tr('save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmbedTokenFormDialog;
